import { CHARACTER_CLASSES, type CharacterClass, type CharacterSummary } from '@d2/schema/characters';
import { useState, type ReactElement, type SyntheticEvent } from 'react';

import { type ApiRefusal } from './api';
import { checkNewCharacter, describeRefusal, messageFor, type FieldMessage } from './forms';

/**
 * The roster: who this account has, who it is about to make, and who it is about
 * to lose.
 *
 * ## The three classes come from the contract, not from this file
 *
 * `CHARACTER_CLASSES` is `@d2/schema`'s own list, which `apps/api` validates
 * against and `docs/GAME-DESIGN.md` decides — *"Melee, caster, ranged. Three
 * skill trees each."* A hard-coded list of three radio buttons here would be a
 * fourth definition of it, and the day a class is added this form would be the
 * one place nobody remembered to change.
 *
 * The **descriptions** beside them are this file's, and they are deliberately
 * flavour rather than rules: what a class actually does is core's, and no
 * number a player could plan around is stated here (`CLAUDE.md`: a damage number
 * computed outside core is a bug — and so is one merely *quoted* outside it).
 *
 * ## Deleting is guarded, because nothing can undo it
 *
 * `DELETE /api/characters/:id` frees the name for whoever wants it next, so a
 * mis-click is not recoverable even in principle. The confirmation asks for the
 * name back — the same shape of guard as typing a repository name, and the only
 * one that survives a player who clicks *yes* by reflex.
 */

/** Flavour, not rules. See the header. */
const CLASS_BLURB: Readonly<Record<CharacterClass, string>> = {
  melee: 'Closes to contact and stays there.',
  caster: 'Fights at range with the elements.',
  ranged: 'Keeps its distance and shoots.',
};

export interface CharacterScreenProps {
  readonly displayName: string;
  readonly characters: readonly CharacterSummary[];
  readonly onPlay: (characterId: string) => void;
  readonly onCreate: (character: { name: string; characterClass: CharacterClass }) => void;
  readonly onDelete: (characterId: string) => void;
  readonly onSignOut: () => void;
  readonly busy: boolean;
  readonly refusal: ApiRefusal | null;
  /** Still fetching the roster. An empty list and an unfetched one are different screens. */
  readonly loading: boolean;
}

export const CharacterScreen = ({
  displayName,
  characters,
  onPlay,
  onCreate,
  onDelete,
  onSignOut,
  busy,
  refusal,
  loading,
}: CharacterScreenProps): ReactElement => {
  const [name, setName] = useState('');
  const [characterClass, setCharacterClass] = useState<string>(CHARACTER_CLASSES[0]);
  const [checked, setChecked] = useState<readonly FieldMessage[]>([]);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState('');

  const submit = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();

    const messages = checkNewCharacter({ name, characterClass });
    setChecked(messages);
    if (messages.length > 0) return;

    // Narrowed by `checkNewCharacter`, which runs `isCharacterClass` — the same
    // guard `apps/api` narrows with.
    onCreate({ name, characterClass: characterClass as CharacterClass });
    setName('');
  };

  const doomed = characters.find((character) => character.id === confirming) ?? null;

  return (
    <section className="panel" data-testid="character-screen">
      <header className="panel__header">
        <h2 className="panel__title">Characters</h2>
        <div className="panel__actions">
          <span className="panel__who" data-testid="signed-in-as">
            {displayName}
          </span>
          <button className="button button--quiet" data-testid="sign-out" type="button" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </header>

      {refusal !== null && (
        <p className="form__error" role="alert" data-testid="character-error">
          {describeRefusal(refusal)}
        </p>
      )}

      {loading ? (
        <p className="panel__lede" data-testid="roster-loading">
          Reading your characters…
        </p>
      ) : characters.length === 0 ? (
        <p className="panel__lede" data-testid="roster-empty">
          No characters yet. Make one below and descend.
        </p>
      ) : (
        <ul className="roster" data-testid="roster">
          {characters.map((character) => (
            <li className="roster__row" key={character.id} data-testid={`character-${character.name}`}>
              <button
                className="roster__play"
                data-testid={`play-${character.name}`}
                type="button"
                disabled={busy}
                onClick={(): void => {
                  onPlay(character.id);
                }}
              >
                <span className="roster__name">{character.name}</span>
                <span className="roster__meta">
                  {character.characterClass} · level {character.level}
                </span>
              </button>
              <button
                className="button button--quiet"
                data-testid={`delete-${character.name}`}
                type="button"
                disabled={busy}
                onClick={(): void => {
                  setConfirming(character.id);
                  setConfirmation('');
                }}
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}

      {doomed !== null && (
        <div className="confirm" role="alertdialog" aria-label="Delete character" data-testid="delete-confirm">
          <p>
            Deleting <strong>{doomed.name}</strong> cannot be undone, and frees the name for anybody else. Type the name
            to confirm.
          </p>
          <input
            className="field__input"
            data-testid="confirm-name"
            type="text"
            value={confirmation}
            onChange={(event): void => {
              setConfirmation(event.target.value);
            }}
          />
          <div className="confirm__actions">
            <button
              className="button button--danger"
              data-testid="confirm-delete"
              type="button"
              disabled={busy || confirmation !== doomed.name}
              onClick={(): void => {
                onDelete(doomed.id);
                setConfirming(null);
              }}
            >
              Delete for good
            </button>
            <button
              className="button button--quiet"
              data-testid="cancel-delete"
              type="button"
              onClick={(): void => {
                setConfirming(null);
              }}
            >
              Keep
            </button>
          </div>
        </div>
      )}

      <form className="form" onSubmit={submit} noValidate>
        <h3 className="panel__subtitle">New character</h3>

        <label className="field">
          <span className="field__label">Name</span>
          <input
            className="field__input"
            data-testid="character-name"
            type="text"
            value={name}
            onChange={(event): void => {
              setName(event.target.value);
            }}
          />
          {messageFor(checked, 'name') !== null && (
            <p className="field__error" data-testid="error-name">
              {messageFor(checked, 'name')}
            </p>
          )}
        </label>

        <fieldset className="classes">
          <legend className="field__label">Class</legend>
          {CHARACTER_CLASSES.map((option) => (
            <label className="classes__option" key={option}>
              <input
                type="radio"
                name="characterClass"
                data-testid={`class-${option}`}
                value={option}
                checked={characterClass === option}
                onChange={(): void => {
                  setCharacterClass(option);
                }}
              />
              <span className="classes__name">{option}</span>
              <span className="classes__blurb">{CLASS_BLURB[option]}</span>
            </label>
          ))}
        </fieldset>

        <button className="button" data-testid="create-character" type="submit" disabled={busy}>
          Create
        </button>
      </form>
    </section>
  );
};
